import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, SDLogo } from '@/components/atoms';
import { auth as authApi, profile as profileApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileState } from '@/contexts/ProfileContext';
import { trackEvent, EVENTS } from '@/lib/analytics';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { reload, savedProfile, analysis } = useProfileState();
  const [status, setStatus] = useState('Signing you in with LinkedIn…');
  const [error, setError] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const oauthError = params.get('error_description') || params.get('error');
      if (oauthError) {
        setError(`LinkedIn returned: ${oauthError}`);
        return;
      }
      if (!code) {
        setError('No authorization code returned by LinkedIn.');
        return;
      }
      try {
        const data = await authApi.linkedinCallback(code, state || '');
        if (data.session_token) localStorage.setItem('sd_token', data.session_token);
        await refresh();
        setStatus('Loading your dashboard…');
        // NOTE: We do NOT rely on the `analysis` / `savedProfile` values from
        // context here — they are captured from the pre-login closure and
        // would still be null even after `await reload()` (React setState is
        // asynchronous). Instead we fetch profile + analysis DIRECTLY from
        // the API with the fresh session token and route from those responses.
        // (Same pattern as `routeAfterAuth()` in Welcome.jsx.)
        const { profile: profApi, analysis: anaApi } = await import('@/lib/api');
        const [prg, prf] = await Promise.all([
          anaApi.latest().catch(() => ({ analysis: null })),
          profApi.get().catch(() => ({ profile: null })),
        ]);
        // Kick off a non-blocking context reload so downstream pages see fresh
        // state; do not await it — routing must not wait on it.
        reload();
        // Auto-subscribe to Sustainability Decoded for NEW LinkedIn OAuth
        // users. Prefer the backend's `is_new_user` flag; fall back to the
        // absence of a saved profile name in the DIRECT API response (not
        // context, which is still hydrating).
        const isNew = data?.is_new_user === true || !prf?.profile?.name;
        if (isNew) {
          try { trackEvent(EVENTS.signup, { method: 'linkedin' }); } catch (_e) { /* no-op */ }
          profileApi
            .newsletterConsent({ newsletter_opt_in: true, source: 'signup_linkedin' })
            .catch(() => { /* non-blocking */ });
        }
        // Route from the FRESH API responses, not context. This is the fix
        // for the "existing account users get sent to onboarding" bug.
        if (prg?.analysis) navigate('/dashboard', { replace: true });
        else if (prf?.profile?.pivot_type) navigate('/onboarding/review', { replace: true });
        else if (prf?.profile?.name) navigate('/onboarding/confirm', { replace: true });
        else navigate('/onboarding/connect', { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
        setError(msg);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-sd-ivory px-6">
      <div className="absolute top-6 left-8"><SDLogo /></div>
      <div className="sd-card p-10 max-w-[480px] w-full text-center animate-fade-in">
        {!error ? (
          <>
            <div className="flex justify-center mb-5"><Spinner size={42} /></div>
            <h2 className="text-[20px] font-semibold text-sd-black mb-2">{status}</h2>
            <p className="text-sd-mid-gray text-[14px]">This usually takes 5–10 seconds.</p>
          </>
        ) : (
          <>
            <div className="text-sd-error text-[40px] mb-3">!</div>
            <h2 className="text-[20px] font-semibold text-sd-black mb-2">We couldn’t complete sign-in</h2>
            <p className="text-sd-mid-gray text-[14px] mb-5">{error}</p>
            <button
              className="sd-btn-primary w-full"
              onClick={() => navigate('/', { replace: true })}
            >
              Try a different sign-in method →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
